import { useState, useEffect } from 'react';
import { get, patch, del, post } from '../../utils/api';
import { formatDateTime } from '../../utils/formatters';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const MessagesViewer = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [replyMessage, setReplyMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const data = await get('/contact-messages');
      setMessages(data);
    } catch (err) {
      setError('Nepodařilo se načíst zprávy');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMessage = async (message) => {
    setSelectedMessage(message);
    setReplyMessage(''); // Reset odpovědi při výběru nové zprávy
    setSuccessMessage(''); // Reset success message
    if (!message.is_read) {
      try {
        await patch(`/contact-messages/${message.id}`, { is_read: true });
        setMessages(prev =>
          prev.map(msg => (msg.id === message.id ? { ...msg, is_read: true } : msg))
        );
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Opravdu chcete smazat tuto zprávu?')) return;
    try {
      await del(`/contact-messages/${id}`);
      setMessages(prev => prev.filter(msg => msg.id !== id));
      if (selectedMessage?.id === id) {
        setSelectedMessage(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      alert('Odpověď nemůže být prázdná');
      return;
    }
    try {
      await post(`/contact-messages/${selectedMessage.id}/reply`, { replyMessage });
      setSuccessMessage('Odpověď byla úspěšně odeslána!');
      
      // Update message in the list to show it was replied to
      setMessages(prev =>
        prev.map(msg => (msg.id === selectedMessage.id ? { ...msg, has_reply: true } : msg))
      );
    } catch (err) {
      console.error(err);
      alert('Chyba při odesílání odpovědi');
    }
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === 'all') return true;
    if (filter === 'read') return msg.is_read;
    if (filter === 'unread') return !msg.is_read;
    return true;
  });

  if (loading && messages.length === 0) {
    return <div className="admin-p-4">Načítání...</div>;
  }

  return (
    <div className="admin-p-4">
      <h1 className="admin-text-2xl admin-font-bold admin-mb-6">Zprávy z kontaktního formuláře</h1>

      {error && (
        <div className="admin-alert admin-alert-error">{error}</div>
      )}

      <div className="admin-mb-4">
        <div className="admin-flex admin-space-x-2">
          <button
            className={`admin-btn ${filter === 'all' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
            onClick={() => setFilter('all')}
          >
            Všechny
          </button>
          <button
            className={`admin-btn ${filter === 'unread' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
            onClick={() => setFilter('unread')}
          >
            Nepřečtené
          </button>
          <button
            className={`admin-btn ${filter === 'read' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
            onClick={() => setFilter('read')}
          >
            Přečtené
          </button>
        </div>
      </div>

      <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-3 admin-gap-6">
        {/* Messages list */}
        <div className="admin-md:col-span-1">
          <div className="admin-card admin-bg-dark-gray">
            <div className="admin-card-header admin-bg-accent-gray">
              Zprávy ({filteredMessages.length})
            </div>
            {filteredMessages.length === 0 ? (
              <div className="admin-p-4 admin-text-center admin-text-gray-500">Žádné zprávy</div>
            ) : (
              <div className="admin-divide-y admin-overflow-y-auto" style={{ maxHeight: '600px' }}>
                {filteredMessages.map(message => (
                  <div
                    key={message.id}
                    className={`admin-p-4 admin-cursor-pointer hover:admin-bg-gray-50 ${selectedMessage?.id === message.id ? 'admin-bg-gray-50' : ''} ${!message.is_read ? 'admin-font-semibold' : ''}`}
                    onClick={() => handleSelectMessage(message)}
                  >
                    <div className="admin-flex admin-justify-between admin-items-start">
                      <div className="admin-truncate">
                        <div className="admin-text-sm admin-font-medium admin-text-white">{message.name}</div>
                        <div className="admin-text-sm admin-text-gray-500 admin-truncate">{message.subject}</div>
                      </div>
                      <div className="admin-flex admin-items-center">
                        {message.has_reply && <div className="admin-mr-2 admin-text-green-500" title="Zpráva má odpověď"><i className="fas fa-reply"></i></div>}
                        {!message.is_read && <div className="admin-bg-blue-500 admin-rounded-full admin-w-2 admin-h-2"></div>}
                      </div>
                    </div>
                    <div className="admin-text-xs admin-text-gray-400 admin-mt-1">{formatDateTime(message.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Message detail */}
        <div className="admin-md:col-span-2">
          {selectedMessage ? (
            <div className="admin-card admin-bg-dark-gray">
              <div className="admin-card-header admin-bg-accent-gray admin-flex admin-justify-between admin-items-center">
                <h2 className="admin-font-medium admin-text-white">{selectedMessage.subject}</h2>
                <div className="admin-flex admin-space-x-2">
                  <button
                    onClick={() => handleDeleteMessage(selectedMessage.id)}
                    className="admin-btn admin-btn-red admin-px-2 admin-py-1"
                    title="Smazat"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>

              <div className="admin-p-6">
                <div className="admin-flex admin-justify-between admin-items-start admin-mb-4 admin-pb-4 admin-border-b admin-border-gray-700">
                  <div>
                    <div className="admin-font-medium admin-text-white">Od: {selectedMessage.name}</div>
                    <div className="admin-text-sm admin-text-gray-500">{selectedMessage.email}</div>
                  </div>
                  <div className="admin-text-sm admin-text-gray-500">{formatDateTime(selectedMessage.created_at)}</div>
                </div>

                <div className="admin-whitespace-pre-wrap admin-text-white admin-mb-6">{selectedMessage.message}</div>

                {/* Reply form */}
                <div className="admin-mt-6 admin-pt-4 admin-border-t admin-border-gray-700">
                  <h3 className="admin-text-lg admin-font-medium admin-text-white admin-mb-2">Odpovědět</h3>
                  
                  {successMessage && (
                    <div className="admin-mb-4 admin-bg-green-500/20 admin-border admin-border-green-500 admin-text-green-500 admin-p-3 admin-rounded admin-flex admin-items-center">
                      <i className="fas fa-check-circle admin-mr-2"></i>
                      {successMessage}
                    </div>
                  )}
                  
                  <div className="admin-mb-4" style={{ height: '300px' }}>
                    <ReactQuill
                      value={replyMessage}
                      onChange={setReplyMessage}
                      theme="snow"
                      style={{ height: '250px' }}
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, false] }],
                          ['bold', 'italic', 'underline'],
                          [{'list': 'ordered'}, {'list': 'bullet'}],
                          ['link'],
                          ['clean']
                        ],
                      }}
                    />
                  </div>
                  <button
                    onClick={handleSendReply}
                    className="admin-btn admin-btn-primary"
                  >
                    <i className="fas fa-reply admin-mr-2"></i>
                    Odeslat odpověď
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="admin-card admin-bg-dark-gray admin-p-8 admin-text-center admin-text-gray-500">
              Vyberte zprávu ze seznamu pro zobrazení detailů
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesViewer;